/**
 * /development/collections/:indexId — one collection.
 *
 * Everyone who can see it (owner, grantee, or an Administrator reading it) gets
 * the documents table and can open a document. Only the **owner** gets the
 * mutations — add / remove documents, build, rename, delete — and the Access
 * panel; the backend enforces the same rule (grantees get `403`).
 *
 * While the collection is `building` the page polls and shows live progress;
 * polling stops when the build is over.
 */

import { useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconDownload,
  IconHammer,
  IconInfoCircle,
  IconPencil,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { useDocumentViewer } from "@/features/solutions/shared/DocumentViewer";
import { RenameModal } from "@/features/projects/RenameModal";
import { createdOn } from "@/features/projects/creatorMeta";
import { ApiError } from "@/shared/api/apiFetch";
import {
  DOCUMENT_INDEX_RESOURCE,
  buildIndex,
  deleteIndex,
  indexDocumentContentUrl,
  removeDocuments,
  updateIndex,
} from "@/shared/api/indexes";
import { downloadFile } from "@/shared/api/integration-browse";
import { promptForReason } from "@/shared/ui/promptForReason";
import { toastApiError, toastSuccess, toastWarning } from "@/shared/ui/toast";
import { AccessPanel } from "./AccessPanel";
import { AddDocumentsPanel } from "./AddDocumentsPanel";
import { BuildProgressCard } from "./BuildProgressCard";
import { SourceIcon } from "./SourceIcon";
import {
  VEEVA_ACCESS_NOTE,
  collectionStatusColor,
  documentStatusColor,
  nestTranslations,
  ownerLabel,
  translationsLabel,
} from "./collectionMeta";
import { useCollection } from "./useCollection";

export function CollectionDetail() {
  const { indexId = "" } = useParams();
  const navigate = useNavigate();
  const getToken = useAccessToken();
  const viewer = useDocumentViewer();
  const { detail, loading, error, reload } = useCollection(indexId);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);

  const hasVeeva = useMemo(
    () => (detail?.documents ?? []).some((d) => d.source_kind === "veeva"),
    [detail],
  );

  if (loading && !detail) {
    return (
      <Group justify="center" p="xl">
        <Loader size="sm" />
      </Group>
    );
  }
  if (!detail) {
    return (
      <Alert variant="light" color="red" title="Could not load the collection">
        {error ?? "Not found."}
      </Alert>
    );
  }

  const owner = detail.is_owner;
  const isBuilding = detail.status === "building";
  const docs = detail.documents;

  const build = async () => {
    setStarting(true);
    try {
      const token = await getToken();
      await buildIndex(token, detail.index_id);
      await reload();
    } catch (err) {
      if (err instanceof ApiError && err.code === "document_index_build_unavailable") {
        toastWarning(
          "Building is not available here",
          "No embedding model is configured in this environment.",
        );
      } else if (err instanceof ApiError && err.code === "document_index_busy") {
        toastWarning("Already building", "A build is already running for this collection.");
        await reload();
      } else {
        toastApiError("Could not start the build", err);
      }
    } finally {
      setStarting(false);
    }
  };

  const removeSelected = async () => {
    const ids = [...selected];
    const reason = await promptForReason(
      `Remove ${ids.length} document${ids.length === 1 ? "" : "s"}`,
      "Reason",
      {
        warning:
          "Their passages leave the collection's index. The source files in Blob or Veeva are not touched.",
        confirmLabel: "Remove",
      },
    );
    if (!reason) return;
    try {
      const token = await getToken();
      const out = await removeDocuments(token, detail.index_id, ids, reason);
      toastSuccess("Removed", `${out.removed} document${out.removed === 1 ? "" : "s"} removed.`);
      setSelected(new Set());
      await reload();
    } catch (err) {
      toastApiError("Could not remove documents", err);
    }
  };

  const remove = async () => {
    const reason = await promptForReason(`Delete “${detail.name}”`, "Reason", {
      warning:
        "This deletes the collection, its index and the platform's own copies (uploaded files and extracted text). Source files in Azure Blob and Veeva are not touched.",
      confirmLabel: "Delete collection",
    });
    if (!reason) return;
    try {
      const token = await getToken();
      await deleteIndex(token, detail.index_id, reason);
      toastSuccess("Deleted", `“${detail.name}” was deleted. Source files were not touched.`);
      navigate("/development/collections");
    } catch (err) {
      toastApiError("Could not delete the collection", err);
    }
  };

  const open = async (documentId: string, name: string) => {
    if (viewer) {
      viewer.openDocument({ path: indexDocumentContentUrl(detail.index_id, documentId), name });
      return;
    }
    setDownloading(documentId);
    try {
      const token = await getToken();
      await downloadFile(token, indexDocumentContentUrl(detail.index_id, documentId), name);
    } catch (err) {
      toastApiError("Could not open the document", err);
    } finally {
      setDownloading(null);
    }
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Group gap="sm">
            <Title order={2}>{detail.name}</Title>
            <Badge variant="light" color={collectionStatusColor(detail.status)}>
              {detail.status}
            </Badge>
            {owner && (
              <Tooltip label="Rename">
                <ActionIcon
                  variant="subtle"
                  aria-label="Rename collection"
                  onClick={() => setRenaming(true)}
                >
                  <IconPencil size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
          <Text size="sm" c="dimmed">
            {detail.description ?? "No description"}
          </Text>
          <Text size="xs" c="dimmed">
            {detail.document_count} documents · {detail.chunk_count} chunks · last built{" "}
            {detail.last_built_at_utc ? createdOn(detail.last_built_at_utc) : "never"} · created by{" "}
            {ownerLabel(detail)} · {createdOn(detail.created_at_utc)}
          </Text>
        </Stack>
        {owner && (
          <Group gap="xs">
            <Button
              leftSection={<IconHammer size={16} />}
              onClick={() => void build()}
              loading={starting}
              disabled={isBuilding || docs.length === 0}
            >
              {detail.last_built_at_utc ? "Rebuild" : "Build"}
            </Button>
            <Button
              variant="default"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={() => void remove()}
              disabled={isBuilding}
            >
              Delete
            </Button>
          </Group>
        )}
      </Group>

      {!owner && (
        <Alert variant="light" color="gray">
          Shared with you by {ownerLabel(detail)}. You can open its documents; only the owner can
          change it.
        </Alert>
      )}

      {hasVeeva && (
        <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />}>
          {VEEVA_ACCESS_NOTE}
        </Alert>
      )}

      {isBuilding && <BuildProgressCard detail={detail} />}

      <Paper withBorder radius="md" p="md">
        <Group justify="space-between" mb="sm">
          <Title order={4}>Documents</Title>
          {owner && (
            <Group gap="xs">
              <Button
                variant="default"
                size="xs"
                color="red"
                leftSection={<IconTrash size={14} />}
                disabled={selected.size === 0 || isBuilding}
                onClick={() => void removeSelected()}
              >
                Remove selected
              </Button>
              <Button
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => setAdding(true)}
                disabled={isBuilding}
              >
                Add documents
              </Button>
            </Group>
          )}
        </Group>
        {docs.length === 0 ? (
          <Text c="dimmed" ta="center" p="lg">
            No documents yet.
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={600}>
            <Table verticalSpacing="xs" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  {owner && <Table.Th style={{ width: 36 }} />}
                  <Table.Th style={{ width: 40 }}>Source</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Pages</Table.Th>
                  <Table.Th>Chunks</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th style={{ width: 48 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {nestTranslations(docs).map(({ doc: d, depth }) => (
                  <Table.Tr key={d.document_id}>
                    {owner && (
                      <Table.Td>
                        <Checkbox
                          aria-label={`Select ${d.display_name}`}
                          checked={selected.has(d.document_id)}
                          onChange={() => toggle(d.document_id)}
                        />
                      </Table.Td>
                    )}
                    <Table.Td>
                      <SourceIcon kind={d.source_kind} />
                    </Table.Td>
                    <Table.Td pl={depth ? "xl" : undefined}>
                      <Group gap={6} wrap="nowrap">
                        {depth === 1 && (
                          <Text size="sm" c="dimmed" aria-hidden>
                            ↳
                          </Text>
                        )}
                        <Text size="sm">{d.display_name}</Text>
                        {d.language && d.derived_from_document_id && (
                          <Badge size="xs" variant="light" color="grape">
                            {d.language.toUpperCase()}
                          </Badge>
                        )}
                      </Group>
                      {depth === 0 && translationsLabel(docs, d.document_id) && (
                        <Text size="xs" c="dimmed">
                          {translationsLabel(docs, d.document_id)}
                        </Text>
                      )}
                      {d.source_kind === "veeva" && d.source_version && (
                        <Text size="xs" c="dimmed">
                          version {d.source_version.replace("_", ".")}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>{d.page_count ?? "—"}</Table.Td>
                    <Table.Td>{d.chunk_count}</Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light" color={documentStatusColor(d.status)}>
                        {d.status}
                      </Badge>
                      {d.status_reason && (d.status === "skipped" || d.status === "failed") && (
                        <Text size="xs" c="dimmed">
                          {d.status_reason}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label="Open original">
                        <ActionIcon
                          variant="subtle"
                          aria-label={`Open ${d.display_name}`}
                          loading={downloading === d.document_id}
                          onClick={() => void open(d.document_id, d.display_name)}
                        >
                          <IconDownload size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Paper>

      {owner && (
        <AccessPanel
          resourceType={DOCUMENT_INDEX_RESOURCE}
          resourceId={detail.index_id}
          ownerUserId={detail.owner_user_id}
          noun="collection"
        />
      )}

      <Modal
        opened={adding}
        onClose={() => {
          setAdding(false);
          void reload();
        }}
        title="Add documents"
        size="xl"
      >
        <AddDocumentsPanel indexId={detail.index_id} onAdded={() => void reload()} />
        <Text size="xs" c="dimmed" mt="md">
          New documents are indexed at the next build.
        </Text>
      </Modal>

      <RenameModal
        opened={renaming}
        onClose={() => setRenaming(false)}
        what="collection"
        name={detail.name}
        description={detail.description}
        onSubmit={async (name, description) => {
          const token = await getToken();
          await updateIndex(token, detail.index_id, { name, description });
          await reload();
        }}
      />
    </Stack>
  );
}
